<template>
  <div>
    <div v-if="loading">
      <div
        v-dompurify-html="
          I18n.t('export_file_instances.generating', { name: exportFileName })
        "
        class="sf-pad-3 sf-align-c"
      />
      <div class="sf-pad-3 sf-align-c">
        <div class="sf-spinner sf-spinner-l sf-inline-block" />
      </div>
    </div>
    <div v-else>
      <div class="sf-pad-2 sf-align-c sf-break-word">
        <div v-if="warningsFound">
          <img
            class="sf-below-2"
            alt=""
            :src="$imagesRequire('export-error.svg')"
          />
          <div
            v-dompurify-html="
              I18n.t(
                'export_file_instances.download_ready_with_potential_issues_html',
                { file_name: exportFileName }
              )
            "
            class="sf-below-2 sf-pad-h-5"
          />
        </div>
        <div v-else>
          <!-- Definietely extract this to separate SvgImage -->
          <svg
            class="sf-below-2"
            width="41px"
            height="41px"
            viewBox="0 0 41 41"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
          >
            <g>
              <rect
                class="sf-c-primary-subtle sf-fill"
                x="0"
                y="0"
                width="41"
                height="41"
                rx="20.5"
              />
              <path
                class="sf-c-primary sf-fill"
                d="M19.5859442,25.8906249 C19.7812569,25.8906249 19.9765696,25.8124998 20.1171948,25.6718746 L26.8359528,18.9531166 C26.976578,18.8124914 27.0547031,18.6171787 27.0547031,18.421866 C27.0547031,18.2265532 26.976578,18.0312405 26.8359528,17.8906153 L25.7734515,16.8281141 C25.6328264,16.6874889 25.4375136,16.6093638 25.2422009,16.6093638 C25.0468882,16.6093638 24.8515754,16.6874889 24.7109503,16.8281141 L19.5859442,21.9609327 L17.2890664,19.6562424 C17.1484413,19.5156173 16.9531285,19.4374922 16.7578158,19.4374922 C16.5625031,19.4374922 16.3671903,19.5156173 16.2265652,19.6562424 L15.1640639,20.7187437 C15.0234387,20.8593689 14.9453136,21.0546816 14.9453136,21.2499943 C14.9453136,21.4453071 15.0234387,21.6406198 15.1640639,21.781245 L19.0546935,25.6718746 C19.1953187,25.8124998 19.3906314,25.8906249 19.5859442,25.8906249 Z"
                fill-rule="nonzero"
              />
            </g>
          </svg>
          <div
            v-dompurify-html="
              I18n.t(
                'export_file_instances.download_ready_without_validation_errors_html',
                { file_name: exportFileName }
              )
            "
            class="sf-below-2 sf-pad-h-5"
          />
        </div>
      </div>
      <div v-if="warningsFound" class="sf-pad-v-3 sf-below-3">
        <div class="sf-layer-rounded sf-layer-border sf-pad-2">
          <div class="sf-title sf-below-2">
            {{ I18n.t('export_file_instances.potential_issues') }}
          </div>
          <div
            v-if="!balancedLedger"
            class="
              sf-layer-rounded
              sf-bg-danger-subtle-2
              sf-c-danger-light-1
              sf-below-1
              sf-pad-h-2
              sf-pad-v-1
            "
            data-html="true"
            data-toggle="tooltip"
            data-placement="top"
            :title="unbalancedLedgerTooltip"
          >
            <div class="sf-fwdb">
              {{ I18n.t('export_file_instances.unbalanced_trial_balance') }}:
              {{ sumOfAllAccounts }}
            </div>
          </div>
          <ExpandableError
            v-if="outdatedLockedValues.length !== 0"
            :title="
              I18n.t('export_file_instances.outdated_locked_values', {
                error_count: outdatedLockedValues.length,
              })
            "
          >
            <div class="sf-overflow-scroll" style="max-height: 300px">
              <div
                v-for="(name, idx) in outdatedLockedValues"
                :key="idx"
                class="sf-pad-h-1 sf-overflow-hidden"
              >
                <div
                  class="
                    sf-layer-rounded sf-bg-white sf-pad-1 sf-below-1 sf-c-text
                  "
                >
                  {{ name }}
                </div>
              </div>
              <div class="sf-pad-h-2 sf-below-1 sf-c-text">
                <a
                  :href="ledgerOutdatedLockedValuesPath"
                  class="sf-link sf-a"
                  >{{
                    I18n.t('export_file_instances.view_outdated_locked_values')
                  }}</a
                >
              </div>
            </div>
          </ExpandableError>
          <ExpandableError
            v-if="validationMessagesToShow.length !== 0"
            :title="
              I18n.t('export_file_instances.validation_errors_count', {
                error_count: validationMessagesToShow.length,
              })
            "
          >
            <div class="sf-overflow-scroll" style="max-height: 300px">
              <div
                v-for="(validationMessage, idx) in validationMessagesToShow"
                :key="idx"
                class="sf-pad-h-1 sf-overflow-hidden"
              >
                <div
                  class="
                    sf-layer-rounded sf-bg-white sf-pad-1 sf-below-1 sf-c-text
                  "
                >
                  {{ validationMessage }}
                </div>
              </div>
            </div>
          </ExpandableError>
          <ExpandableError
            v-if="unreconciledItems.length !== 0"
            :title="
              I18n.t('export_file_instances.unreconciled_reconciliations', {
                error_count: unreconciledItems.length,
              })
            "
          >
            <div class="sf-overflow-scroll" style="max-height: 300px">
              <div
                v-for="([name, link], idx) in unreconciledItems"
                :key="idx"
                class="sf-pad-h-1 sf-overflow-hidden"
              >
                <div
                  class="
                    sf-layer-rounded sf-bg-white sf-pad-1 sf-below-1 sf-c-text
                  "
                >
                  <a :href="link" class="sf-link sf-a">{{ name }}</a>
                </div>
              </div>
            </div>
          </ExpandableError>
          <div
            v-if="workflowProgressIsPotentialIssue"
            class="sf-layer-rounded sf-bg-snow-light sf-pad-h-2 sf-pad-v-1"
          >
            <div class="sf-level sf-level-between">
              <div class="sf-fwdb">
                {{ I18n.t('export_file_instances.workflow_progress_is') }}
                {{ workflowProgressPercentage }}%
              </div>
              <div style="width: 70px">
                <div class="sf-progress sf-progress-tiny sf-bg-snow-dark">
                  <div
                    class="sf-progress-complete sf-bg-primary"
                    :style="`width: ${workflowProgressPercentage}%`"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-dompurify-html="renderedHTML" />
      <a
        v-if="sureSyncAvailable"
        href="#"
        class="sf-btn sf-full sf-align-c sf-below-1"
        :class="{
          'tw-pointer-events-none tw-opacity-50': sureSyncLoading,
        }"
        @click.prevent="submitSureSyncRendering"
      >
        <template v-if="sureSyncLoading">
          {{ I18n.t('sure_sync.generating') }}
          <span class="sf-spinner sf-inline-block" />
        </template>
        <template v-else>
          {{ I18n.t('sure_sync.render_button') }}
        </template>
      </a>
    </div>
  </div>
</template>

<script>
import $ from 'jquery'
import ExpandableError from '@app/shared/vue/components/expandable-error'
import decompressWorkflowProgresses from 'src/application/workflows/utils/workflow_progress_decompressor'
import SFetch from '@app/utils/sfetch'
import { useToastStore } from '@app/shared/pinia/toast'
import getPiniaInstance from '@app/shared/pinia/instance'

const ExportFileInstanceModalContent = {
  components: {
    ExpandableError,
  },
  props: {
    exportFileInstanceId: Number,
    exportFileInstanceChannel: String,
    exportFileInstanceLastId: Number,
    workflowProgressChannel: String,
    workflowProgressLastId: Number,
    exportFileName: String,
    renderedHTML: String,
    ledgerId: Number,
    workflowId: Number,
    balancedLedger: Boolean,
    unbalancedLedgerTooltip: String,
    sumOfAllAccounts: Number,
    firmOrSilverfinAdmin: Boolean,
    sureSyncAvailable: Boolean,
    sureSyncRenderingUrl: String,
  },
  data() {
    return {
      validationMessages: [],
      unreconciledItems: [],
      outdatedLockedValues: [],
      unreconciledNamesReceived: false,
      exportFileCreated: false,
      workflowProgressPercentage: null,
      exportWithWorkflowContext: false,
      emptyFile: false,
      pit: false,
      sureSyncLoading: false,
      sureSyncStopPolling: null,
    }
  },
  computed: {
    ledgerOutdatedLockedValuesPath() {
      return Silverfin.Routes.outdated_locked_values_ledger_path(
        Silverfin.currentFirmId,
        Silverfin.currentCompanyId,
        this.ledgerId
      )
    },
    loading() {
      if (this.exportWithWorkflowContext) {
        return !(
          this.exportFileCreated &&
          this.workflowProgressPercentage !== null &&
          this.unreconciledNamesReceived
        )
      } else {
        return !this.exportFileCreated
      }
    },
    warningsFound() {
      if (this.exportWithWorkflowContext) {
        return (
          this.warningsWithoutWorkflow ||
          this.workflowProgressPercentage < 100 ||
          this.unreconciledItems.length > 0 ||
          this.outdatedLockedValues.length > 0
        )
      } else {
        return this.warningsWithoutWorkflow
      }
    },
    warningsWithoutWorkflow() {
      return this.validationErrorsPresent || this.emptyFile
    },
    workflowProgressIsPotentialIssue() {
      if (
        this.exportWithWorkflowContext &&
        this.workflowProgressPercentage !== 100 &&
        this.workflowProgressPercentage !== 'n/a'
      ) {
        return true
      } else {
        return false
      }
    },
    allowDownloading() {
      return (
        (!this.warningsWithoutWorkflow || this.firmOrSilverfinAdmin) &&
        !this.emptyFile
      )
    },
    validationMessagesToShow() {
      if (this.emptyFile)
        return [I18n.t('export_file_instances.empty_file_error')]
      else if (this.firmOrSilverfinAdmin || !this.validationErrorsPresent)
        if (this.pitValidationErrorsPresent)
          return [I18n.t('export_file_instances.pit_xsd_validation_error')]
        else {
          return this.validationMessages
        }
      else return [I18n.t('export_file_instances.invalid_file_error')]
    },
    pitValidationErrorsPresent() {
      return this.pit && this.validationErrorsPresent
    },
    validationErrorsPresent() {
      return this.validationMessages.length > 0
    },
  },
  watch: {
    loading: function (newLoading) {
      if (newLoading === false) this.createExportFileInstanceAuditLog()
    },
  },
  beforeUnmount() {
    if (this.sureSyncStopPolling) {
      this.sureSyncStopPolling()
      this.sureSyncStopPolling = null
    }
  },
  created() {
    Silverfin.Polling.subscribe(
      this.exportFileInstanceChannel,
      (data) => {
        if (data.id === this.exportFileInstanceId) {
          this.validationMessages = data.validation_messages
          this.emptyFile = data.empty_file
          this.exportFileCreated = true
          this.pit = data.pit
        }
      },
      this.exportFileInstanceLastId
    )

    if (this.workflowProgressChannel) {
      Silverfin.Polling.subscribe(
        this.workflowProgressChannel,
        this.newStatusesReceived,
        this.workflowProgressLastId
      )
      this.newStatusRequest()
      this.exportWithWorkflowContext = true
    }
  },
  updated() {
    // Why is this in the `updated` lifecycle hook instead of `watched`?
    // The download button is not part of the Vue template, but it'll be rendered as part
    // of the HTML (modal content) coming from the server (along with an optional download
    // warning). That HTML will be shown when `exportFileCreated` is set to true, but we
    // need DOM to have the button present so that we can disable it.
    if (!this.allowDownloading) this.disableDownloadButton()
    if (this.validationErrorsPresent) this.disableSubmitButtons()
  },
  methods: {
    newStatusRequest() {
      const statusUrl = Silverfin.Routes.status_ledger_company_workflow_path(
        Silverfin.currentFirmId,
        Silverfin.currentCompanyId,
        this.ledgerId,
        this.workflowId
      )
      $.get({ url: statusUrl })
    },

    newStatusesReceived(data) {
      const accounts = []
      const outdatedAccounts = []
      const reconTexts = []
      const { statuses, progress } = data

      // TODO: This is very implicit / complicated logic, this needs to be improved https://gitlab.silverfin.com/development/silverfin/-/issues/9408
      if (statuses) {
        const mappedStatuses = decompressWorkflowProgresses(statuses)
        const starredElements = mappedStatuses.filter(
          (elm) => elm['starred'] === true
        )
        starredElements
          .filter((elm) => {
            if (elm['reconciliation_not_necessary'] === true) {
              return (
                elm['reconciliation_not_necessary_and_no_required_inputs'] ===
                  false && elm['required_keys_missing'] === true
              )
            } else {
              // recon_is_necessary
              return elm['reconciled'] === false
            }
          })
          .forEach((element) => {
            if (element['object_type'] === 'account') {
              accounts.push(element['object_id'])
            } else {
              reconTexts.push(element['object_id'])
            }
          })

        starredElements.forEach((element) => {
          if (element['outdated']) {
            outdatedAccounts.push(element['object_id'])
          }
        })
      }

      const options = { company_workflow_id: this.workflowId }
      const retrieveReconciliationNamesPath =
        Silverfin.Routes.reconciliation_names_ledger_export_file_instance_path(
          Silverfin.currentFirmId,
          Silverfin.currentCompanyId,
          this.ledgerId,
          this.exportFileInstanceId,
          options
        )
      $.post({
        url: retrieveReconciliationNamesPath,
        data: { accounts, outdatedAccounts, reconTexts },
        success: ({
          outdatedAccounts = [],
          accounts = [],
          reconTexts = [],
        } = {}) => {
          this.outdatedLockedValues = outdatedAccounts
          this.unreconciledItems = [...accounts, ...reconTexts]
          this.unreconciledNamesReceived = true
        },
      })

      if (progress) {
        if (progress.total <= 0) {
          this.workflowProgressPercentage = 'n/a'
        } else {
          this.workflowProgressPercentage = Math.floor(
            (progress.done / progress.total) * 100
          )
        }
      }
    },
    createExportFileInstanceAuditLog() {
      const auditLogExportFileCreationUrl =
        Silverfin.Routes.create_audit_log_ledger_export_file_instance_path(
          Silverfin.currentFirmId,
          Silverfin.currentCompanyId,
          this.ledgerId,
          this.exportFileInstanceId
        )
      const postData = {}

      if (this.validationErrorsPresent) {
        postData['validation_errors_count'] = this.validationMessages.length
      }

      if (this.exportWithWorkflowContext) {
        if (this.workflowProgressIsPotentialIssue) {
          postData['workflow_completion'] = this.workflowProgressPercentage
        }

        if (this.unreconciledItems.length > 0) {
          postData['unreconciled_items_count'] = this.unreconciledItems.length
        }

        if (this.outdatedLockedValues.length > 0) {
          postData['outdated_locked_values_count'] =
            this.outdatedLockedValues.length
        }
      }

      $.post({ url: auditLogExportFileCreationUrl, data: postData })
    },
    disableDownloadButton() {
      const downloadButton = document.querySelector(
        "[data-id='ef-download-btn']"
      )
      if (downloadButton) {
        downloadButton.classList.add('sf-disabled')
      }
    },
    disableSubmitButtons() {
      document
        .querySelectorAll("[data-id='ef-submit-btn']")
        .forEach((submitButton) => {
          submitButton.classList.add('sf-disabled')
        })
    },
    async submitSureSyncRendering() {
      if (this.sureSyncLoading) return

      this.sureSyncLoading = true

      try {
        const data = await SFetch.json(this.sureSyncRenderingUrl, {
          method: 'POST',
        })
        this.subscribeSureSyncChannel(data.channel, data.lastId)
      } catch {
        this.sureSyncRaiseToast(
          'danger',
          I18n.t('sure_sync.submit_error_title'),
          I18n.t('sure_sync.error_message')
        )
        this.sureSyncLoading = false
      }
    },
    subscribeSureSyncChannel(channel, lastId) {
      this.sureSyncStopPolling = Silverfin.Polling.subscribe(
        channel,
        (data) => {
          this.sureSyncLoading = false

          if (data.status === 'completed' && data.download_url) {
            this.sureSyncTriggerDownload(data.download_url)
            this.sureSyncRaiseToast(
              'success',
              I18n.t('sure_sync.export_ready_title'),
              I18n.t('sure_sync.export_ready_message')
            )
            this.dismissModal()
          } else if (data.status === 'failed') {
            this.sureSyncRaiseToast(
              'danger',
              data.error_title || I18n.t('sure_sync.submit_error_title'),
              data.error_message || I18n.t('sure_sync.error_message')
            )
          }
        },
        lastId
      )
    },
    dismissModal() {
      const modal = this.$el.closest('.sf-modal')
      if (modal) {
        const dismiss = modal.querySelector('[data-dismiss="sf-modal"]')
        if (dismiss) dismiss.click()
      }
    },
    sureSyncRaiseToast(variant, title, message) {
      const toastStore = useToastStore(getPiniaInstance())
      toastStore.raiseToast({
        id: 'sure-sync-rendering',
        title,
        message,
        variant,
        size: 'lg',
        actions: [],
      })
    },
    sureSyncTriggerDownload(url) {
      const link = document.createElement('a')
      link.href = url
      link.download = ''
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}

export default ExportFileInstanceModalContent
</script>
